import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { CATEGORIES, COLLECTIONS } from "@/lib/content";
import ProductCard from "@/components/ProductCard";
import MoonDivider from "@/components/MoonDivider";

export default function Collections() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "";
  const collection = params.get("collection") || "";

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category) q.category = category;
    if (collection) q.collection = collection;
    api.get("/products", { params: q })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, collection]);

  const clearFilters = () => setParams(new URLSearchParams());
  const activeLabel = collection || category;

  return (
    <div className="max-w-7xl mx-auto px-6 py-14" data-testid="collections-page">
      <div className="text-center mb-4">
        <p className="font-body uppercase tracking-[0.35em] text-xs text-gold mb-3">Browse By</p>
        <h1 className="font-serif text-5xl sm:text-6xl text-cream">Collections</h1>
      </div>
      <MoonDivider />

      {/* Category cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 my-12">
        {CATEGORIES.map((c) => (
          <button
            key={c.name}
            onClick={() => setParams({ category: c.name })}
            data-testid={`browse-category-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`group relative aspect-[4/5] overflow-hidden border transition-colors ${category === c.name ? "border-gold" : "border-white/5 hover:border-gold/40"}`}
          >
            <img src={mediaUrl(c.image)} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-4 text-center">
              <h3 className="font-serif text-lg text-cream group-hover:text-gold transition-colors">{c.name}</h3>
            </div>
          </button>
        ))}
      </div>

      {/* Collection chips */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {COLLECTIONS.map((col) => (
          <button
            key={col.name}
            onClick={() => setParams({ collection: col.name })}
            data-testid={`browse-collection-${col.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`px-5 py-2 font-body uppercase tracking-[0.15em] text-[11px] border transition-colors ${collection === col.name ? "bg-gold text-black border-gold" : "border-white/15 text-white/60 hover:border-gold/50 hover:text-gold"}`}
          >
            {col.name}
          </button>
        ))}
        {activeLabel && (
          <button onClick={clearFilters} data-testid="clear-filters" className="px-5 py-2 font-body uppercase tracking-[0.15em] text-[11px] text-white/40 hover:text-gold underline underline-offset-4">
            Clear
          </button>
        )}
      </div>

      {activeLabel && (
        <h2 className="font-serif text-3xl text-cream text-center mb-8" data-testid="active-filter-label">{activeLabel}</h2>
      )}

      {loading ? (
        <p className="text-center font-serif text-xl text-white/40 py-16">Curating…</p>
      ) : products.length === 0 ? (
        <div className="text-center py-16">
          <p className="font-serif text-xl text-white/40 mb-4">Nothing here yet.</p>
          <Link to="/shop" className="text-gold font-body uppercase tracking-widest text-xs border-b border-gold/40 pb-1">Shop All</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
